'use client';
import { useEffect, useState, useCallback } from 'react';
import { supabase } from './lib/supabase';
import VitrinePage from './vitrine/page';
import AureusLogo from './components/AureusLogo';
import { useSessionGuard, SessionKickedModal } from './lib/useSessionGuard';
import { lireErreurLienAuth, TXT_LIEN_MORT, langueLienMort } from './lib/auth-link-error';
import { sessionNeedsMFA } from './lib/security/mfa-gate';
import dynamic from 'next/dynamic';

const DashboardLayout = dynamic(() => import('./(dashboard)/layout-client'), { ssr: false });
const MfaGate = dynamic(() => import('./components/MfaGate'), { ssr: false });
const OnboardingClient = dynamic(() => import('./pages/OnboardingClient'), { ssr: false });

// Emails admin qui n'ont pas besoin d'onboarding
const ADMIN_EMAILS = ['info@aureussocial.be', 'info@aureus-ia.com'];

// ── Sonde de session, exécutée AVANT la première peinture (22/08/2026) ──────
// Le rendu serveur de « / » est désormais la vitrine, pour que Google et les
// crawlers IA aient du contenu (voir le bloc de rendu plus bas). Un utilisateur
// DÉJÀ connecté ne doit pas voir passer la vitrine avant son dashboard.
//
// Ce script est présent dans le HTML servi, donc il s'exécute pendant l'analyse
// du document — avant tout paint et avant l'hydratation React. Il ne touche
// PAS l'arbre React (sinon on recréerait un écart d'hydratation) : il pose un
// attribut sur <html>, et c'est le CSS ci-dessous qui décide quoi montrer.
//
// Supabase range son jeton sous une clé « sb-<ref-projet>-auth-token » : on la
// détecte par motif plutôt qu'en dur, pour survivre à un changement de projet.
const SONDE_SESSION = `(function(){try{for(var i=0;i<localStorage.length;i++){var k=localStorage.key(i);if(k&&/^sb-.+-auth-token$/.test(k)){document.documentElement.setAttribute('data-aureus-session','1');return}}}catch(e){}})()`;

// Par défaut (robot, visiteur anonyme, stockage vide) : la vitrine est visible
// et le splash masqué — c'est le cas qui compte pour le référencement.
// Session détectée : on inverse, sans flash.
const CSS_SPLASH = `#aureus-splash{display:none}` +
  `[data-aureus-session="1"] #aureus-splash{display:flex;align-items:center;justify-content:center;height:100vh;background:#0c0b09}` +
  `[data-aureus-session="1"] #aureus-vitrine{display:none}`;

export default function Home() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [needsOnboarding, setNeedsOnboarding] = useState(false);
  const [demoRejected, setDemoRejected] = useState(null);
  const [lienMort, setLienMort] = useState(null);
  // Porte 2FA : session ouverte mais 2e facteur pas encore fourni (lien magique,
  // lien de reset, impersonation, ou F5 pendant l'écran TOTP du login).
  const [mfaGate, setMfaGate] = useState(null);

  // Session unique — un seul appareil à la fois
  const handleForceLogout = useCallback(async () => {
    if (supabase) await supabase.auth.signOut();
    setUser(null);
  }, []);

  const { kicked, logoutSession } = useSessionGuard(user, handleForceLogout);

  // Lu AVANT l'effet d'auth (React exécute les effets dans l'ordre de déclaration) :
  // le client Supabase nettoie le hash au passage, il faut donc le capter en premier.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const err = lireErreurLienAuth(window.location.hash);
    if (!err) return;
    setLienMort(err);
    // On retire le hash pour qu'un F5 ne réaffiche pas l'erreur éternellement.
    try { window.history.replaceState({}, '', window.location.pathname + window.location.search); } catch (e) {}
  }, []);

  useEffect(() => {
    async function checkAuth() {
      if (!supabase) {
        setLoading(false);
        setUser({ email: 'demo@aureus-ia.com', role: 'admin' });
        return;
      }

      const { data: { session } } = await supabase.auth.getSession();
      const currentUser = session?.user || null;

      // Si vrai user Supabase existe → nettoyer tout résidu de demo token
      if (currentUser) {
        try { localStorage.removeItem('aureus_demo_token'); } catch(e) {}
      }

      // ⛔ PORTE 2FA — AVANT toute lecture de données et avant tout rendu applicatif.
      // Une session peut naître sans passer par le formulaire de connexion (lien
      // magique, lien de reset, impersonation) : le contrôle 2FA du formulaire ne
      // voyait rien de tout ça. Ici il porte sur la session elle-même, donc il
      // couvre tous les chemins d'entrée. (Incident fondateur 05/08/2026.)
      if (currentUser && await sessionNeedsMFA(supabase)) {
        setMfaGate({ email: currentUser.email });
        setLoading(false);
        return;
      }

      // Si pas de session Supabase, checker si demo token dans URL uniquement
      // (le localStorage demo token ne s'active que s'il n'y a jamais eu de vrai login sur cet appareil)
      if (!currentUser && typeof window !== 'undefined') {
        const params = new URLSearchParams(window.location.search);
        const urlToken = params.get('demo_token');
        const hadRealLogin = (() => { try { return localStorage.getItem('aureus_had_real_login') === 'true'; } catch(e) { return false; } })();
        const savedToken = !hadRealLogin ? (() => { try { return localStorage.getItem('aureus_demo_token'); } catch(e) { return null; } })() : null;
        const demoToken = urlToken || savedToken;
        if (demoToken) {
          try {
            let deviceId;
            try { deviceId = localStorage.getItem('aureus_device_id'); } catch(e) {}
            if (!deviceId) { deviceId = Math.random().toString(36).slice(2) + Date.now().toString(36); try { localStorage.setItem('aureus_device_id', deviceId); } catch(e) {} }
            const r = await fetch('/api/demo-token?token=' + encodeURIComponent(demoToken) + '&device_id=' + encodeURIComponent(deviceId));
            const d = await r.json();
            if (d.valid) {
              const demoRole = d.role || 'readonly';
              try { localStorage.setItem('aureus_demo_token', demoToken); } catch(e) {}
              // token requis côté serveur — sinon /api/demo rejette la requête
              await fetch('/api/demo?token=' + encodeURIComponent(demoToken), { method: 'POST' }).catch(() => {});
              window.history.replaceState({}, '', window.location.pathname + '?demo=true');
              setUser({ email: 'demo-prospect@aureus-ia.com', role: demoRole, app_metadata: { role: demoRole }, user_metadata: { role: demoRole, demo: true } });
              setLoading(false);
              return;
            } else {
              try { localStorage.removeItem('aureus_demo_token'); } catch(e) {}
              if (d.reason) setDemoRejected(d.reason);
            }
          } catch(e) {}
        }
      }

      setUser(currentUser);

      // Marquer cet appareil comme ayant eu un vrai login — bloque l'auto-demo futur
      if (currentUser) {
        try { localStorage.setItem('aureus_had_real_login', 'true'); } catch(e) {}
      }

      // ── LIEN PROFOND (?page=) — ex. CTA email « Encoder mes prestations » ──
      // Les clics depuis un email perdent souvent le #hash, et le dashboard a 3 voies
      // de restauration (useState init, hash, clé par-user) qui peuvent restaurer la
      // « dernière page » et gagner la course contre le ?page=. On verrouille donc la
      // cible ICI, AVANT que le dashboard ne monte, dans les 3 voies → plus de course.
      if (typeof window !== 'undefined') {
        try {
          const _dp = new URLSearchParams(window.location.search).get('page');
          if (_dp && /^[a-z0-9_]+$/.test(_dp) && _dp.length < 60) {
            localStorage.setItem('aureus_current_page', _dp);                 // useState init (dashboard)
            if (currentUser?.id) localStorage.setItem('aureus_page_' + currentUser.id, _dp); // restauration par-user
            window.history.replaceState({}, '', window.location.pathname + '#' + _dp);        // voie hash prioritaire
          }
        } catch (e) {}
      }

      // Une société existe si : entreprise auto-onboardée (entreprises.user_id)
      // OU dossier client RATTACHÉ (clients.client_user_id — patron invité/lié
      // par le staff, via /api/clients qui scope côté serveur). Sans le 2e cas,
      // un patron rattaché retombait sur le wizard « espace client » et pouvait
      // créer sa société en DOUBLON du dossier géré par le secrétariat.
      const hasCompany = async (uid) => {
        const { data: ent } = await supabase.from('entreprises').select('nom').eq('user_id', uid).limit(1);
        if (ent?.[0]?.nom && ent[0].nom.length > 0) return true;
        try {
          const { data: { session } } = await supabase.auth.getSession();
          const token = session?.access_token;
          if (!token) return false;
          const res = await fetch('/api/clients', { headers: { Authorization: 'Bearer ' + token } });
          const j = await res.json().catch(() => ({}));
          return !!(res.ok && j?.data?.length);
        } catch { return false; }
      };

      // Verifier si premiere connexion (onboarding requis)
      if (currentUser && !ADMIN_EMAILS.includes(currentUser.email)) {
        // Les collaborateurs invités (rh, commercial, comptable, rh_entreprise)
        // n'ont pas d'entreprise à configurer — bypass onboarding
        const userRole = currentUser.app_metadata?.role || '';
        const isCollaborateur = ['rh','commercial','comptable','rh_entreprise','employe','readonly'].includes(userRole);
        if (isCollaborateur) {
          setNeedsOnboarding(false);
        } else {
          const { data } = await supabase
            .from('app_state')
            .select('val')
            .eq('key', `onboarding_done_${currentUser.id}`)
            .limit(1);

          const onboardingDone = data?.[0]?.val === 'true';

          if (!onboardingDone) {
            setNeedsOnboarding(!(await hasCompany(currentUser.id)));
          }
        }
      }

      setLoading(false);

      supabase.auth.onAuthStateChange(async (_event, session) => {
        const u = session?.user || null;
        // Même porte sur les changements de session en cours de vie (rafraîchissement
        // de jeton, retour de lien magique dans l'onglet déjà ouvert).
        if (u && await sessionNeedsMFA(supabase)) {
          setUser(null);
          setMfaGate({ email: u.email });
          return;
        }
        setMfaGate(null);
        setUser(u);
        if (u && !ADMIN_EMAILS.includes(u.email)) {
          const uRole = u.app_metadata?.role || '';
          const isCollab = ['rh','commercial','comptable','rh_entreprise','employe','readonly'].includes(uRole);
          if (isCollab) {
            setNeedsOnboarding(false);
          } else {
            const { data } = await supabase
              .from('app_state')
              .select('val')
              .eq('key', `onboarding_done_${u.id}`)
              .limit(1);
            if (!data?.[0]) {
              setNeedsOnboarding(!(await hasCompany(u.id)));
            }
          }
        } else {
          setNeedsOnboarding(false);
        }
      });
    }
    checkAuth();
  }, []);

  const tLien = TXT_LIEN_MORT[langueLienMort()];

  // Porte 2FA : rien d'autre ne se monte tant que le code n'est pas validé.
  // Après validation, la session passe en aal2 dans le stockage → on recharge,
  // et le contrôle ci-dessus laisse alors passer. (Remonté avant le bloc vitrine
  // le 22/08/2026 : pendant `loading`, mfaGate vaut toujours null, donc l'ordre
  // relatif ne change rien — mais les deux états qui rendent la vitrine doivent
  // rester contigus, voir ci-dessous.)
  if (mfaGate) return (
    <MfaGate
      email={mfaGate.email}
      onVerified={() => { if (typeof window !== 'undefined') window.location.reload(); }}
      onCancel={() => { setMfaGate(null); setUser(null); }}
    />
  );

  // ── SEO : ce que les robots voient réellement sur « / » ────────────────────
  // Jusqu'au 22/08/2026, `loading` démarrait à true et le rendu SERVEUR de la
  // page d'accueil était l'écran « Chargement… » : mesuré à 112 caractères de
  // texte et zéro <h1> dans le HTML, sur la page qui doit porter les requêtes
  // de tête. Google devait exécuter le JS pour voir quoi que ce soit — et les
  // ~30 crawlers IA autorisés dans public/robots.txt (GPTBot, ClaudeBot,
  // PerplexityBot…) n'exécutent PAS de JS : ils ne lisaient donc rien du tout,
  // ce qui annulait en silence tout le travail GEO/AEO de docs/30.01.
  //
  // Désormais le rendu serveur = la vitrine. Deux propriétés à préserver :
  //  1. serveur et premier rendu client produisent le MÊME arbre → aucun écart
  //     d'hydratation (c'est pour ça que le splash se pilote en CSS, pas en JSX) ;
  //  2. `loading` et « pas de session » rendent le même bloc → la vitrine n'est
  //     jamais démontée puis remontée entre les deux états.
  if (loading || !user) return (
    <>
      <script dangerouslySetInnerHTML={{ __html: SONDE_SESSION }} />
      <style dangerouslySetInnerHTML={{ __html: CSS_SPLASH }} />
      {loading && (
        <div id="aureus-splash">
          <div style={{ textAlign: 'center' }}>
            <AureusLogo size={64} priority style={{ marginBottom: 14, justifyContent: 'center' }} />
            <div style={{ fontSize: 28, fontWeight: 800, color: '#c6a34e', letterSpacing: '2px' }}>AUREUS</div>
            <div style={{ fontSize: 11, color: '#5e5c56', marginTop: 4 }}>Chargement...</div>
          </div>
        </div>
      )}
      <div id="aureus-vitrine">
        {demoRejected && (
          <div style={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 9999, background: '#dc2626', color: '#fff', padding: '14px 20px', textAlign: 'center', fontSize: 14, fontWeight: 600 }}>
            ⛔ {demoRejected}
          </div>
        )}
        {lienMort && (
          <div style={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 9999, background: '#1a1505', borderBottom: '2px solid #c6a34e', color: '#e8e6e0', padding: '16px 20px', textAlign: 'center', fontSize: 14 }}>
            <div style={{ fontWeight: 800, color: '#c6a34e', marginBottom: 4 }}>🔑 {tLien.titre}</div>
            <div style={{ fontSize: 12.5, color: '#b9b6ae', maxWidth: 640, margin: '0 auto 10px' }}>{tLien.corps}</div>
            <a href="/login" style={{ display: 'inline-block', padding: '9px 20px', borderRadius: 9, background: 'linear-gradient(135deg,#E2BC63,#A6863A)', color: '#1a1505', fontSize: 13, fontWeight: 700, textDecoration: 'none' }}>
              {tLien.bouton}
            </a>
          </div>
        )}
        <link rel="alternate" hrefLang="fr-BE" href="https://www.aureussocial.be" />
        <link rel="alternate" hrefLang="nl-BE" href="https://www.aureussocial.be/nl" />
        <VitrinePage />
      </div>
    </>
  );

  // Session déconnectée depuis un autre appareil
  if (kicked) {
    return <SessionKickedModal onConfirm={handleForceLogout} />;
  }

  // Premiere connexion client → onboarding
  if (needsOnboarding) {
    return (
      <OnboardingClient
        user={user}
        onComplete={() => setNeedsOnboarding(false)}
      />
    );
  }

  return <DashboardLayout key={"dashboard-" + (user?.id || "loading")} user={user} onSessionLogout={logoutSession} />;
}
